/* eslint-disable no-console -- 개발 전용 로거. 운영 빌드에서는 호출부가 isDev 로 걸러진다. */
import { isServer } from '../constants'
import { maskSensitive } from './maskSensitive'

const ERROR_STATUS_THRESHOLD = 400
const SERVER_ERROR_STATUS_THRESHOLD = 500
const MAX_TEXT_LENGTH = 300
const SUMMARY_KEY_COUNT = 3
const FULL_BODY_ENV = 'HTTP_LOG_BODY'

const ANSI = {
  reset: '\x1b[0m',
  bold: '\x1b[1m',
  gray: '\x1b[90m',
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
} as const

const BROWSER_COLOR = {
  success: '#16a34a',
  clientError: '#ea580c',
  serverError: '#dc2626',
  dim: '#9ca3af',
} as const

type TStatusKind = 'success' | 'clientError' | 'serverError'

const TERMINAL_STATUS_COLOR: Record<TStatusKind, string> = {
  success: ANSI.green,
  clientError: ANSI.yellow,
  serverError: ANSI.red,
}

export interface IHttpLogParams {
  method?: string
  url?: string
  status?: number
  requestBody?: unknown
  data: unknown
}

const isErrorStatus = (status?: number): boolean =>
  (status ?? 0) >= ERROR_STATUS_THRESHOLD

const getStatusKind = (status?: number): TStatusKind => {
  if ((status ?? 0) >= SERVER_ERROR_STATUS_THRESHOLD) return 'serverError'
  if (isErrorStatus(status)) return 'clientError'
  return 'success'
}

const trimText = (value: unknown): unknown =>
  typeof value === 'string' && value.length > MAX_TEXT_LENGTH
    ? `${value.slice(0, MAX_TEXT_LENGTH)}… (${value.length}자)`
    : value

const summarizeBody = (data: unknown): string => {
  if (data === undefined || data === null || data === '') return '본문 없음'
  if (Array.isArray(data)) return `배열 ${data.length}개`
  if (typeof data === 'string') return `텍스트 ${data.length}자`
  if (typeof data !== 'object') return String(data)

  const keys = Object.keys(data)
  const shown = keys.slice(0, SUMMARY_KEY_COUNT).join(', ')
  return keys.length > SUMMARY_KEY_COUNT ? `{ ${shown}, … }` : `{ ${shown} }`
}

const shouldPrintFullBody = (status?: number): boolean =>
  isErrorStatus(status) || process.env[FULL_BODY_ENV] === 'true'

const colorizeJson = (json: string | undefined): string =>
  (json ?? 'undefined').replace(
    /("(?:\\.|[^"\\])*"(\s*:)?|\b(?:true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    match => {
      if (match.startsWith('"')) {
        return match.endsWith(':')
          ? `${ANSI.cyan}${match}${ANSI.reset}`
          : `${ANSI.green}${match}${ANSI.reset}`
      }
      if (match === 'null') return `${ANSI.gray}${match}${ANSI.reset}`
      if (match === 'true' || match === 'false') {
        return `${ANSI.magenta}${match}${ANSI.reset}`
      }
      return `${ANSI.yellow}${match}${ANSI.reset}`
    }
  )

/** pino(JSON 한 줄)와 overrideConsole 을 우회한다. */
const logToTerminal = ({
  method,
  url,
  status,
  requestBody,
  data,
}: IHttpLogParams): void => {
  const failed = isErrorStatus(status)
  const stream = failed ? process.stderr : process.stdout

  if (!shouldPrintFullBody(status)) {
    const line = `[${status ?? ''}] ${method ?? ''} ${url ?? ''} · ${summarizeBody(data)}`
    stream.write(
      stream.isTTY ? `${ANSI.green}${line}${ANSI.reset}\n` : `${line}\n`
    )
    return
  }

  const sections = [
    ...(requestBody === undefined
      ? []
      : [
          `${ANSI.gray}request${ANSI.reset}`,
          JSON.stringify(maskSensitive(requestBody), null, 2),
        ]),
    JSON.stringify(trimText(data), null, 2),
  ]

  if (!stream.isTTY) {
    stream.write(
      `\n[${status ?? ''}] ${method ?? ''} ${url ?? ''}\n${sections.join('\n')}\n`
    )
    return
  }

  const statusColor = TERMINAL_STATUS_COLOR[getStatusKind(status)]
  const head =
    `${ANSI.bold}${statusColor}[${status ?? ''}]${ANSI.reset} ` +
    `${ANSI.blue}${method ?? ''}${ANSI.reset} ` +
    `${ANSI.gray}${url ?? ''}${ANSI.reset}`

  stream.write(`\n${head}\n${sections.map(colorizeJson).join('\n')}\n`)
}

const logToBrowser = ({
  method,
  url,
  status,
  requestBody,
  data,
}: IHttpLogParams): void => {
  const color = BROWSER_COLOR[getStatusKind(status)]

  console.groupCollapsed(
    `%c[${status ?? ''}]%c ${method ?? ''} %c${url ?? ''}`,
    `color:${color};font-weight:bold`,
    'color:inherit',
    `color:${BROWSER_COLOR.dim}`
  )
  if (requestBody !== undefined) {
    console.log('request ', maskSensitive(requestBody))
  }
  console.log('response', trimText(data))
  console.groupEnd()
}

// Edge 런타임에는 process.stdout 이 없다. 서버라도 없으면 console 로 떨어뜨린다.
const hasStdout = typeof process !== 'undefined' && Boolean(process.stdout)

export const logHttpResponse = (params: IHttpLogParams): void => {
  if (isServer && hasStdout) {
    logToTerminal(params)
    return
  }
  logToBrowser(params)
}
